import { addAptosExtension } from '@dynamic-labs-sdk/aptos';
import { addBitcoinExtension } from '@dynamic-labs-sdk/bitcoin';
import {
  createDynamicClient,
  getWalletConnectCatalogWalletByWalletProviderKey,
  initializeClient,
  isMobile,
  onEvent,
} from '@dynamic-labs-sdk/client';
import { addEvmExtension } from '@dynamic-labs-sdk/evm';
import { addWalletConnectEvmExtension } from '@dynamic-labs-sdk/evm/wallet-connect';
import {
  addPhantomRedirectSolanaExtension,
  addSolanaExtension,
} from '@dynamic-labs-sdk/solana';
import { addWalletConnectSolanaExtension } from '@dynamic-labs-sdk/solana/wallet-connect';
import { addStarknetExtension } from '@dynamic-labs-sdk/starknet';
import { addSuiExtension } from '@dynamic-labs-sdk/sui';
import { addTronExtension } from '@dynamic-labs-sdk/tron';
import { addZerodevExtension } from '@dynamic-labs-sdk/zerodev';
import { toast } from 'sonner';

import { getDevSettings } from '../../utils/getDevSettings';
import { initializeDevSettingsFromUrl } from '../../utils/initializeDevSettingsFromUrl';
import { installPhantomLogInterceptor } from '../../utils/phantomDebugLog/phantomDebugLog';
import { getPreferredDeepLink } from '../components/walletConnect/getPreferredDeepLink';

installPhantomLogInterceptor();

initializeDevSettingsFromUrl();

const devSettings = getDevSettings();

// Trailing slash is stripped because the SDK's api-core constructs paths as
// `${apiBaseUrl}/endpoint`, which would produce a double slash otherwise.
const apiBaseUrl = new URL(
  devSettings.apiBaseUrlPath,
  devSettings.apiBaseUrlOrigin
).href.replace(/\/$/, '');

export const dynamicClient = createDynamicClient({
  autoInitialize: false,

  coreConfig: {
    apiBaseUrl,
  },

  environmentId: devSettings.environmentId,
  logLevel: 'debug',
  metadata: {
    name: 'Dynamic',
    universalLink: window.location.origin,
  },
});

// Add debugging logs

const logDebugEvent = <T extends keyof DynamicEvents>(event: T) => {
  onEvent({
    event,
    listener: (...args: unknown[]) => {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`${event}`);
      args.forEach((arg) => {
        // eslint-disable-next-line no-console
        console.log(JSON.stringify(arg, null, 2));
      });
      // eslint-disable-next-line no-console
      console.groupEnd();
    },
  });
};

logDebugEvent('userChanged');
logDebugEvent('tokenChanged');
logDebugEvent('walletAccountsChanged');
logDebugEvent('initStatusChanged');
logDebugEvent('projectSettingsChanged');
logDebugEvent('walletProviderChanged');
logDebugEvent('logout');
logDebugEvent('deviceRegistrationCompleted');
logDebugEvent('deviceRegistrationCompletedInAnotherTab');

// Init
void initializeClient();

// Conditionally add extensions based on settings
if (devSettings.extensions.evm) {
  addEvmExtension();
}

if (devSettings.extensions.zerodev) {
  addZerodevExtension();
}

if (devSettings.extensions.solana) {
  addSolanaExtension();
}

if (devSettings.extensions.bitcoin) {
  addBitcoinExtension();
}

if (devSettings.extensions.sui) {
  addSuiExtension();
}

if (devSettings.extensions.aptos) {
  addAptosExtension();
}

if (devSettings.extensions.tron) {
  addTronExtension();
}

if (devSettings.extensions.starknet) {
  addStarknetExtension();
}

if (devSettings.extensions.walletConnectEvm) {
  void addWalletConnectEvmExtension();
}

if (devSettings.extensions.phantomRedirectSolana) {
  void addPhantomRedirectSolanaExtension({
    onCloseTab: () => window.close(),
    url: new URL(window.location.href),
  });
}

if (devSettings.extensions.walletConnectSolana) {
  void addWalletConnectSolanaExtension();
}

onEvent({
  event: 'walletConnectUserActionRequested',
  listener: async ({ walletProviderKey }) => {
    if (isMobile()) {
      const walletConnectCatalogWallet =
        await getWalletConnectCatalogWalletByWalletProviderKey({
          walletProviderKey,
        });

      if (!walletConnectCatalogWallet?.deeplinks) {
        return;
      }

      const deepLink = getPreferredDeepLink(
        walletConnectCatalogWallet.deeplinks
      );

      if (deepLink) {
        window.open(deepLink, '_blank');

        return;
      }
    }

    toast.info('Please approve the action in your wallet application', {
      duration: 5000,
    });
  },
});
